import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ChevronDown, ChevronUp, CircleCheck, Eye, UserRound} from 'lucide-react';
import {getStatusClass} from '@/shared/constants/orderStatus';
import {Link} from 'react-router-dom';
import {useMutation} from '@tanstack/react-query';
import type {ProviderOrderSort} from '@mld/shared/constants/providerOrders';
import type {StatusTone, StatusToneStyles} from './statusTone';

export type PatientOrdersSortOption = ProviderOrderSort;

export interface OrderSearchFields {
    oid: string;
    firstName: string;
    lastName: string;
    email: string;
}

export interface PatientOrderRow {
    id: number;
    userId: number | null;
    patient: string;
    panel: string;
    panelType: string;
    sampleCapturedDate: string;
    labReceivedDate: string;
    sampleCapturedAt?: string | null;
    labReceivedAt?: string | null;
    statusDate?: string | null;
    statusLabel: string;
    tone: StatusTone;
    actionLabel: string;
}

export interface PatientOrdersTableProps {
    orders: PatientOrderRow[];
    ordersLoading: boolean;
    ordersError: string | null;
    statusToneStyles: StatusToneStyles;
    onSearchOrders: (
        fields: OrderSearchFields
    ) => Promise<{orders: PatientOrderRow[]; toast: {error: boolean; message: string}}>;
    onResetSearch: () => void;
    page: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    sortOption: PatientOrdersSortOption;
    onSortChange: (option: PatientOrdersSortOption) => void;
    isSearchActive: boolean;
    returnTo?: string;
    canSearch?: boolean;
    canViewPii?: boolean;
}

const sortOptions: Array<{label: string; value: PatientOrdersSortOption}> = [
    {label: 'Most Recent', value: 'recent'},
    {label: 'Oldest First', value: 'oldest'},
    {label: 'Patient Name (A–Z)', value: 'name'},
];

type SortableColumn = 'name' | 'captured' | 'received';

const columnSortValues: Record<SortableColumn, {asc: PatientOrdersSortOption; desc: PatientOrdersSortOption}> = {
    name: {asc: 'name', desc: 'name_desc'},
    captured: {asc: 'captured_asc', desc: 'captured_desc'},
    received: {asc: 'received_asc', desc: 'received_desc'},
};

type PageItem = number | 'ellipsis';

const buildPageItems = (current: number, total: number): PageItem[] => {
    if (total <= 7) {
        return Array.from({length: total}, (_, idx) => idx + 1);
    }

    const items: PageItem[] = [];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    items.push(1);
    if (start > 2) items.push('ellipsis');
    for (let page = start; page <= end; page += 1) {
        items.push(page);
    }
    if (end < total - 1) items.push('ellipsis');
    items.push(total);

    return items;
};

const PatientOrdersTable: React.FC<PatientOrdersTableProps> = ({
    orders,
    ordersLoading,
    ordersError,
    statusToneStyles,
    onSearchOrders,
    onResetSearch,
    page,
    pageSize,
    totalCount,
    onPageChange,
    onPageSizeChange,
    sortOption,
    onSortChange,
    isSearchActive,
    returnTo,
    canSearch = true,
    canViewPii = true,
}) => {
    const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
    const [isPageSizeMenuOpen, setIsPageSizeMenuOpen] = useState(false);
    const pageSizeMenuRef = useRef<HTMLDivElement | null>(null);
    const [searchFields, setSearchFields] = useState<OrderSearchFields>({
        oid: '',
        firstName: '',
        lastName: '',
        email: '',
    });
    const [searchToast, setSearchToast] = useState<{error: boolean; message: string}>({
        error: false,
        message: '',
    });
    useEffect(() => {
        if (!canSearch) {
            setIsSearchPanelOpen(false);
        }
    }, [canSearch]);

    useEffect(() => {
        if (!isPageSizeMenuOpen) return;

        const handleOutsideClick = (event: MouseEvent) => {
            if (!pageSizeMenuRef.current) return;
            if (!pageSizeMenuRef.current.contains(event.target as Node)) {
                setIsPageSizeMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isPageSizeMenuOpen]);

    const searchOrdersMutation = useMutation({
        mutationFn: (fields: OrderSearchFields) => onSearchOrders(fields),
        onSuccess: ({toast}) => {
            setSearchToast(toast);
        },
        onError: () => {
            setSearchToast({error: true, message: 'Error searching orders. Please try again.'});
        },
    });

    const handleSearchInputChange = useCallback(
        (field: keyof OrderSearchFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setSearchFields((prev) => ({
                ...prev,
                [field]: value,
            }));
        },
        []
    );

    const handleSearchSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!canSearch) {
                return;
            }
            setSearchToast({error: false, message: ''});
            searchOrdersMutation.mutate(searchFields);
        },
        [canSearch, searchFields, searchOrdersMutation]
    );

    const handleResetFilters = useCallback(() => {
        setSearchFields({
            oid: '',
            firstName: '',
            lastName: '',
            email: '',
        });
        setSearchToast({error: false, message: ''});
        setIsSearchPanelOpen(false);
        onResetSearch();
    }, [onResetSearch]);

    const buildPatientLaunchUrl = useCallback((order: PatientOrderRow) => {
        const userId = order.userId;
        if (typeof userId !== 'number' || userId <= 0) {
            return '#';
        }

        return `/launch?userId=${encodeURIComponent(String(userId))}`;
    }, []);

    const columnSortDirection = useCallback(
        (column: SortableColumn): 'asc' | 'desc' | null => {
            if (sortOption === columnSortValues[column].asc) return 'asc';
            if (sortOption === columnSortValues[column].desc) return 'desc';
            return null;
        },
        [sortOption]
    );

    const handleColumnSort = useCallback(
        (column: SortableColumn) => {
            const direction = columnSortDirection(column);
            onSortChange(direction === 'asc' ? columnSortValues[column].desc : columnSortValues[column].asc);
        },
        [columnSortDirection, onSortChange]
    );

    const columnSortIndicator = useCallback(
        (column: SortableColumn) => {
            const direction = columnSortDirection(column);
            const baseClass = 'h-3 w-3 transition-colors';
            return (
                <span className="flex flex-col items-center -space-y-1">
                    <ChevronUp
                        className={`${baseClass} ${
                            direction === 'asc'
                                ? 'text-mld-text-secondary'
                                : 'text-mld-muted-text group-hover:text-mld-muted-text'
                        }`}
                    />
                    <ChevronDown
                        className={`${baseClass} ${
                            direction === 'desc'
                                ? 'text-mld-text-secondary'
                                : 'text-mld-muted-text group-hover:text-mld-muted-text'
                        }`}
                    />
                </span>
            );
        },
        [columnSortDirection]
    );

    const sortableHeaderProps = useCallback(
        (column: SortableColumn) => {
            const direction = columnSortDirection(column);
            return {
                role: 'columnheader' as const,
                'aria-sort':
                    direction === 'asc'
                        ? ('ascending' as const)
                        : direction === 'desc'
                          ? ('descending' as const)
                          : ('none' as const),
            };
        },
        [columnSortDirection]
    );

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const clampedPage = Math.min(Math.max(page, 1), totalPages);
    const startItem = totalCount === 0 ? 0 : (clampedPage - 1) * pageSize + 1;
    const endItem = totalCount === 0 ? 0 : Math.min(clampedPage * pageSize, totalCount);
    const pageItems = useMemo(() => buildPageItems(clampedPage, totalPages), [clampedPage, totalPages]);

    return (
        <div className="min-w-0">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                    <h2 className="text-[27px] font-medium text-mld-text-primary mb-2 ">Patient Orders</h2>
                    {!canViewPii && (
                        <span className="mb-2 rounded-full border border-mld-panel-border px-3 py-1 text-xs font-semibold text-mld-muted-text">
                            Limited access
                        </span>
                    )}
                </div>
                <div className="flex flex-wrap gap-3 text-sm text-mld-muted-text">
                    {canSearch && (
                        <button
                            type="button"
                            className="flex items-center gap-1 hover:text-mld-primary"
                            aria-expanded={isSearchPanelOpen}
                            aria-controls="patientOrders-filterPanel"
                            onClick={() => setIsSearchPanelOpen((prev) => !prev)}>
                            Filter by
                            <ChevronDown className="h-4 w-4 text-mld-muted-text" />
                        </button>
                    )}
                    <div className="relative">
                        <button
                            type="button"
                            className="flex items-center gap-1 hover:text-mld-primary"
                            aria-haspopup="menu"
                            aria-expanded={isSortMenuOpen}
                            aria-controls="patientOrders-sortMenu"
                            onClick={() => setIsSortMenuOpen((prev) => !prev)}>
                            Sort by
                            <ChevronDown className="h-4 w-4 text-mld-muted-text" />
                        </button>
                        {isSortMenuOpen && (
                            <div
                                id="patientOrders-sortMenu"
                                role="menu"
                                aria-label="Sort patient orders"
                                className="absolute right-0 z-10 mt-2 w-46 rounded-2xl border border-mld-panel-border bg-mld-background p-2 shadow-lg">
                                {sortOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={sortOption === option.value}
                                        className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm ${
                                            sortOption === option.value
                                                ? 'bg-mld-menu-background text-mld-primary'
                                                : 'text-mld-text-primary hover:bg-mld-menu-background'
                                        }`}
                                        onClick={() => {
                                            onSortChange(option.value);
                                            setIsSortMenuOpen(false);
                                        }}>
                                        {option.label}
                                        {sortOption === option.value && (
                                            <CircleCheck className="h-4 w-4 text-mld-primary" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {canSearch && isSearchPanelOpen && (
                <div
                    id="patientOrders-filterPanel"
                    role="region"
                    aria-label="Order filters"
                    className="mb-4 rounded-2xl border border-mld-panel-border bg-mld-menu-background p-4">
                    <form onSubmit={handleSearchSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label
                                    htmlFor="providerSearch-first"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                    First Name
                                </label>
                                <input
                                    id="providerSearch-first"
                                    type="text"
                                    value={searchFields.firstName}
                                    onChange={handleSearchInputChange('firstName')}
                                    placeholder="Optional"
                                    className="w-full rounded-lg border border-mld-panel-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="providerSearch-last"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                    Last Name
                                </label>
                                <input
                                    id="providerSearch-last"
                                    type="text"
                                    value={searchFields.lastName}
                                    onChange={handleSearchInputChange('lastName')}
                                    placeholder="Optional"
                                    className="w-full rounded-lg border border-mld-panel-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="providerSearch-email"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                    Email
                                </label>
                                <input
                                    id="providerSearch-email"
                                    type="email"
                                    value={searchFields.email}
                                    onChange={handleSearchInputChange('email')}
                                    placeholder="Optional"
                                    className="w-full rounded-lg border border-mld-panel-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="providerSearch-oid"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                    Order ID
                                </label>
                                <input
                                    id="providerSearch-oid"
                                    type="text"
                                    value={searchFields.oid}
                                    onChange={handleSearchInputChange('oid')}
                                    placeholder="Optional"
                                    className="w-full rounded-lg border border-mld-panel-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    disabled={searchOrdersMutation.isPending}
                                    className="rounded-lg bg-mld-primary px-4 py-2 text-white shadow hover:bg-mld-primary/90 disabled:cursor-not-allowed disabled:opacity-60">
                                    {searchOrdersMutation.isPending ? 'Searching...' : 'Search Orders'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleResetFilters}
                                    className="rounded-lg border border-mld-panel-border px-4 py-2 text-mld-text-secondary hover:text-mld-primary">
                                    Reset
                                </button>
                            </div>
                            {searchToast.message && (
                                <div
                                    role="status"
                                    aria-live="polite"
                                    className={`rounded-lg px-3 py-2 text-sm text-white ${
                                        searchToast.error ? 'bg-mld-danger' : 'bg-mld-success'
                                    }`}>
                                    {searchToast.message}
                                </div>
                            )}
                        </div>
                    </form>
                </div>
            )}

            {ordersError && <p className="text-sm text-mld-danger">Error loading orders: {ordersError}</p>}
            {isSearchActive && !ordersLoading && (
                <p className="mt-2 text-sm text-mld-muted-text">Displaying {orders.length} filtered order(s).</p>
            )}

            <div className="overflow-hidden rounded-2xl border border-mld-panel-border text-mld-text-primary">
                {/* Header - hidden on mobile, grid on md+ */}
                <div className="hidden md:grid md:grid-cols-[minmax(160px,1fr)_72px_120px_120px_100px_90px] lg:grid-cols-[minmax(160px,1fr)_72px_115px_115px_100px_90px] xl:grid-cols-[1fr_100px_140px_140px_120px_90px] bg-mld-menu-background border-b border-mld-panel-border">
                    <span className="px-6 py-3" {...sortableHeaderProps('name')}>
                        <button
                            type="button"
                            onClick={() => handleColumnSort('name')}
                            className="group inline-flex items-center gap-2 text-left hover:text-mld-primary">
                            Patient Name
                            {columnSortIndicator('name')}
                        </button>
                    </span>
                    <span className="px-6 py-3">Type</span>
                    <span className="px-6 py-3" {...sortableHeaderProps('captured')}>
                        <button
                            type="button"
                            onClick={() => handleColumnSort('captured')}
                            className="group inline-flex items-center gap-2 text-left hover:text-mld-primary">
                            Sample Captured
                            {columnSortIndicator('captured')}
                        </button>
                    </span>
                    <span className="px-6 py-3" {...sortableHeaderProps('received')}>
                        <button
                            type="button"
                            onClick={() => handleColumnSort('received')}
                            className="group inline-flex items-center gap-2 text-left hover:text-mld-primary">
                            Lab Received
                            {columnSortIndicator('received')}
                        </button>
                    </span>
                    <span className="px-6 py-3">Status</span>
                    <span className="px-6 py-3">Action</span>
                </div>

                {/* Body */}
                <div className="md:grid md:grid-cols-[minmax(160px,1fr)_72px_120px_120px_100px_90px] lg:grid-cols-[minmax(160px,1fr)_72px_115px_115px_100px_90px] xl:grid-cols-[1fr_100px_140px_140px_120px_90px]">
                    {ordersLoading ? (
                        <div className="col-span-full flex items-center justify-center px-6 py-8 text-sm text-mld-muted-text bg-mld-background">
                            Loading latest patient orders…
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="col-span-full flex items-center justify-center px-6 py-8 text-sm text-mld-muted-text bg-mld-background">
                            No patient orders found.
                        </div>
                    ) : (
                        orders.map((order, index) => {
                            const toneStyle = statusToneStyles[order.tone];
                            const ToneIcon = toneStyle.icon;
                            const viewResultsHref = order.userId
                                ? `/provider/patient/${order.userId}`
                                : `/result/${order.id}`;
                            const rowBg = index % 2 === 0 ? 'bg-mld-menu-background' : 'bg-mld-background';

                            return (
                                <div
                                    key={order.id}
                                    className={`col-span-full grid grid-cols-1 md:grid-cols-subgrid ${rowBg} hover:bg-mld-primary/5 transition-colors`}>
                                    <div className="md:border-r md:border-mld-panel-border px-4 py-2 flex items-center">
                                        <p className="text-xs uppercase tracking-[0.2em] md:hidden mr-2">Patient</p>
                                        <p>{order.patient}</p>
                                    </div>

                                    <div className="md:border-r md:border-mld-panel-border px-4 py-2 flex items-center">
                                        <p className="text-xs uppercase tracking-[0.2em] md:hidden mr-2">Type</p>
                                        <p>{order.panelType || 'VP'}</p>
                                    </div>

                                    <div className="md:border-r md:border-mld-panel-border px-4 py-2 flex items-center">
                                        <p className="text-xs uppercase tracking-[0.2em] md:hidden mr-2">
                                            Sample Captured
                                        </p>
                                        <p>{order.sampleCapturedDate}</p>
                                    </div>

                                    <div className="md:border-r md:border-mld-panel-border px-4 py-2 flex items-center">
                                        <p className="text-xs uppercase tracking-[0.2em] md:hidden mr-2">
                                            Lab Received
                                        </p>
                                        <p>{order.labReceivedDate}</p>
                                    </div>

                                    <div className="md:border-r md:border-mld-panel-border px-4 py-2 flex items-center gap-2">
                                        <p className="text-xs uppercase tracking-[0.2em] md:hidden mr-2">Status</p>
                                        <div className={`flex items-center gap-1 ${getStatusClass(order.statusLabel)}`}>
                                            {order.statusLabel}
                                            {ToneIcon && <ToneIcon className="h-4 w-4" />}
                                        </div>
                                    </div>

                                    <div className="px-4 py-2 flex items-center gap-3">
                                        <Link
                                            to={viewResultsHref}
                                            state={returnTo ? {returnTo} : undefined}
                                            rel="noopener noreferrer"
                                            aria-label={`${order.actionLabel} for ${order.patient}`}
                                            title={order.actionLabel}
                                            className={`rounded-md p-1 transition hover:bg-mld-menu-background ${toneStyle.action}`}>
                                            <Eye className="h-4 w-4" />
                                        </Link>
                                        <a
                                            href={buildPatientLaunchUrl(order)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Open dashboard for ${order.patient}`}
                                            title="Open patient dashboard"
                                            aria-disabled={!order.userId}
                                            className={`rounded-md p-1 transition hover:bg-mld-menu-background ${toneStyle.action} ${
                                                order.userId ? '' : 'pointer-events-none opacity-50'
                                            }`}>
                                            <UserRound className="h-4 w-4" />
                                        </a>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {totalCount > 0 && (
                <div className="mt-3 flex flex-wrap items-center justify-end gap-3 text-xs text-mld-muted-text">
                    <div className="flex items-center gap-2">
                        <span>Rows per page</span>
                        <div className="relative" ref={pageSizeMenuRef}>
                            <button
                                type="button"
                                aria-haspopup="menu"
                                aria-expanded={isPageSizeMenuOpen}
                                aria-controls="patientOrders-pageSizeMenu"
                                className="flex h-7 items-center justify-between gap-1 rounded-md border border-mld-panel-border bg-mld-menu-background px-2 text-mld-text-primary hover:bg-mld-background focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                onClick={() => setIsPageSizeMenuOpen((prev) => !prev)}>
                                <span>{pageSize}</span>
                                <ChevronDown className="h-3.5 w-3.5 text-mld-text-primary" />
                            </button>
                            {isPageSizeMenuOpen && (
                                <div
                                    id="patientOrders-pageSizeMenu"
                                    role="menu"
                                    aria-label="Rows per page"
                                    className="absolute right-0 z-10 mt-2 w-20 rounded-xl border border-mld-panel-border bg-mld-background p-1 shadow-lg">
                                    {[10, 15, 25, 50].map((size) => (
                                        <button
                                            key={size}
                                            type="button"
                                            role="menuitemradio"
                                            aria-checked={pageSize === size}
                                            className={`flex w-full items-center justify-between rounded-lg px-2 py-1 text-sm ${
                                                pageSize === size
                                                    ? 'bg-mld-menu-background text-mld-primary'
                                                    : 'text-mld-text-primary hover:bg-mld-menu-background'
                                            }`}
                                            onClick={() => {
                                                onPageSizeChange(size);
                                                setIsPageSizeMenuOpen(false);
                                            }}>
                                            {size}
                                            {pageSize === size && <CircleCheck className="h-3 w-3 text-mld-primary" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <span>
                        {startItem}–{endItem} of {totalCount}
                    </span>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => onPageChange(1)}
                            disabled={clampedPage <= 1}
                            aria-label="First page"
                            className={`rounded-md border border-mld-panel-border px-2 py-1 ${
                                clampedPage <= 1
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'bg-mld-menu-background hover:bg-mld-background'
                            }`}>
                            «
                        </button>
                        <button
                            type="button"
                            onClick={() => onPageChange(clampedPage - 1)}
                            disabled={clampedPage <= 1}
                            aria-label="Previous page"
                            className={`rounded-md border border-mld-panel-border px-2 py-1 ${
                                clampedPage <= 1
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'bg-mld-menu-background hover:bg-mld-background'
                            }`}>
                            ‹
                        </button>
                        {pageItems.map((item, index) =>
                            item === 'ellipsis' ? (
                                <span key={`ellipsis-${index}`} className="px-2 text-mld-muted-text">
                                    …
                                </span>
                            ) : (
                                <button
                                    key={item}
                                    type="button"
                                    onClick={() => onPageChange(item)}
                                    aria-current={item === clampedPage ? 'page' : undefined}
                                    className={`rounded-md border border-mld-panel-border px-2 py-1 ${
                                        item === clampedPage
                                            ? 'bg-mld-primary text-white'
                                            : 'bg-mld-menu-background hover:bg-mld-background text-mld-text-primary'
                                    }`}>
                                    {item}
                                </button>
                            )
                        )}
                        <button
                            type="button"
                            onClick={() => onPageChange(clampedPage + 1)}
                            disabled={clampedPage >= totalPages}
                            aria-label="Next page"
                            className={`rounded-md border border-mld-panel-border px-2 py-1 ${
                                clampedPage >= totalPages
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'bg-mld-menu-background hover:bg-mld-background'
                            }`}>
                            ›
                        </button>
                        <button
                            type="button"
                            onClick={() => onPageChange(totalPages)}
                            disabled={clampedPage >= totalPages}
                            aria-label="Last page"
                            className={`rounded-md border border-mld-panel-border px-2 py-1 ${
                                clampedPage >= totalPages
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'bg-mld-menu-background hover:bg-mld-background'
                            }`}>
                            »
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientOrdersTable;
